import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Modal from '@mui/material/Modal'
import Stack from '@mui/material/Stack'
import { useTranslate } from '@repo/unicity'
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import FlipLeftIcon from './assets/flip-left.svg?react'
import FlipRightIcon from './assets/flip-right.svg?react'

const MIN_CROP_PX = 132

interface ProfilePictureToolProps {
    open: boolean
    /** Object URL or data URL of the picked source image. */
    imgSrc: string | null
    onCancel: () => void
    /** Receives the cropped image as a JPEG Blob. */
    onConfirm: (blob: Blob) => void | Promise<void>
    uploading?: boolean
}

const initialCrop: Crop = {
    unit: '%',
    x: 0,
    y: 0,
    width: 100,
    height: 100
}

const loadImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = url
    })

const rotateImage = async (src: string, degrees: number): Promise<string> => {
    const image = await loadImage(src)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return src

    const orientationChanged = Math.abs(degrees) === 90 || Math.abs(degrees) === 270
    canvas.width = orientationChanged ? image.height : image.width
    canvas.height = orientationChanged ? image.width : image.height

    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.rotate((degrees * Math.PI) / 180)
    ctx.drawImage(image, -image.width / 2, -image.height / 2)

    return new Promise(resolve => {
        canvas.toBlob(
            blob => {
                if (!blob) {
                    resolve(src)
                    return
                }
                resolve(URL.createObjectURL(blob))
            },
            'image/jpeg',
            1
        )
    })
}

const cropToBlob = (image: HTMLImageElement, crop: PixelCrop): Promise<Blob> => {
    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    canvas.width = Math.max(crop.width, 1)
    canvas.height = Math.max(crop.height, 1)
    const ctx = canvas.getContext('2d')

    return new Promise((resolve, reject) => {
        if (!ctx) {
            reject(new Error('No 2d context'))
            return
        }
        ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height)
        canvas.toBlob(
            blob => {
                if (!blob) {
                    reject(new Error('Canvas is empty'))
                    return
                }
                resolve(blob)
            },
            'image/jpeg',
            1
        )
    })
}

const ProfilePictureTool = ({ open, imgSrc, onCancel, onConfirm, uploading = false }: ProfilePictureToolProps) => {
    const { translate } = useTranslate()
    const imageRef = useRef<HTMLImageElement | null>(null)
    const [workingSrc, setWorkingSrc] = useState<string | null>(null)
    const [crop, setCrop] = useState<Crop>(initialCrop)
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null)
    const rotationOwnedRef = useRef<string[]>([])

    useEffect(() => {
        if (!open) return
        setWorkingSrc(imgSrc)
        setCrop(initialCrop)
        setCompletedCrop(null)
    }, [open, imgSrc])

    useEffect(() => {
        return () => {
            rotationOwnedRef.current.forEach(url => URL.revokeObjectURL(url))
            rotationOwnedRef.current = []
        }
    }, [])

    const handleRotate = async (degrees: number) => {
        if (!workingSrc) return
        const rotated = await rotateImage(workingSrc, degrees)
        if (rotated.startsWith('blob:')) {
            rotationOwnedRef.current.push(rotated)
        }
        setWorkingSrc(rotated)
        setCrop(initialCrop)
        setCompletedCrop(null)
    }

    const handleConfirm = async () => {
        if (!imageRef.current || !completedCrop || completedCrop.width < 1 || completedCrop.height < 1) {
            return
        }
        const blob = await cropToBlob(imageRef.current, completedCrop)
        await onConfirm(blob)
    }

    return (
        <Modal open={open} onClose={uploading ? undefined : onCancel} data-testid="profile-picture-tool-modal">
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 384,
                    maxWidth: '92vw',
                    bgcolor: '#fff',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    outline: 'none'
                }}
                data-testid="profile-picture-tool-box-button-handle-confirm">
                <Box
                    sx={{
                        m: '16px 16px 5px 16px',
                        bgcolor: '#DDDDDD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 150,
                        '& .ReactCrop__image': {
                            maxHeight: 'calc(80vh - 272px)',
                            maxWidth: '100%',
                            minHeight: `${MIN_CROP_PX}px`,
                            minWidth: `${MIN_CROP_PX}px`
                        }
                    }}
                    data-testid="profile-picture-tool-box-1">
                    {workingSrc && (
                        <ReactCrop
                            crop={crop}
                            aspect={1}
                            ruleOfThirds
                            minWidth={MIN_CROP_PX}
                            minHeight={MIN_CROP_PX}
                            keepSelection
                            onChange={c => setCrop(c)}
                            onComplete={c => setCompletedCrop(c)}
                            data-testid="profile-picture-tool-react-crop">
                            <img
                                ref={imageRef}
                                src={workingSrc}
                                alt="profile preview"
                                onLoad={e => {
                                    const target = e.currentTarget
                                    const size = Math.min(target.width, target.height)
                                    setCrop({
                                        unit: 'px',
                                        x: (target.width - size) / 2,
                                        y: (target.height - size) / 2,
                                        width: size,
                                        height: size
                                    })
                                    setCompletedCrop({
                                        unit: 'px',
                                        x: (target.width - size) / 2,
                                        y: (target.height - size) / 2,
                                        width: size,
                                        height: size
                                    })
                                }}
                                data-testid="profile-picture-tool-img"
                            />
                        </ReactCrop>
                    )}
                </Box>

                <Stack
                    direction="row"
                    justifyContent="space-around"
                    alignItems="center"
                    sx={{ mb: '20px', px: '16px' }}
                    data-testid="profile-picture-tool-stack">
                    <Box
                        role="button"
                        onClick={() => handleRotate(-90)}
                        sx={{
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                        data-testid="profile-picture-tool-box-2">
                        <Box component={FlipLeftIcon} sx={{ width: 37, height: 32 }} data-testid="profile-picture-tool-box-3" />
                    </Box>
                    <Box
                        role="button"
                        onClick={() => handleRotate(90)}
                        sx={{
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                        data-testid="profile-picture-tool-box-4">
                        <Box component={FlipRightIcon} sx={{ width: 37, height: 32 }} data-testid="profile-picture-tool-box-5" />
                    </Box>
                </Stack>

                <Box sx={{ width: '100%', height: '1px', bgcolor: '#f0f0f0' }} data-testid="profile-picture-tool-box-6" />

                <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="space-evenly"
                    sx={{ p: '16px' }}
                    data-testid="profile-picture-tool-stack-button-handle-confirm">
                    <Button
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        disableElevation
                        disabled={uploading || !completedCrop}
                        onClick={handleConfirm}
                        variant="contained"
                        sx={{
                            backgroundColor: '#0da9ef',
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: '16px',
                            textTransform: 'none',
                            width: '115px',
                            height: '48px',
                            borderRadius: '32px',
                            boxShadow: 'none',
                            transition: 'none',
                            '&:hover, &:active, &:focus, &.Mui-focusVisible': {
                                backgroundColor: '#0da9ef',
                                color: '#fff',
                                boxShadow: 'none'
                            }
                        }}
                        data-testid="profile-picture-tool-button-handle-confirm">
                        {translate('confirm')}
                    </Button>
                    <Button
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        disableElevation
                        disabled={uploading}
                        onClick={onCancel}
                        sx={{
                            backgroundColor: '#fff',
                            color: 'grey',
                            border: '1px solid grey',
                            fontWeight: 500,
                            fontSize: '16px',
                            textTransform: 'none',
                            width: '115px',
                            height: '48px',
                            borderRadius: '32px',
                            boxShadow: 'none',
                            transition: 'none',
                            '&:hover, &:active, &:focus, &.Mui-focusVisible': {
                                backgroundColor: '#fff',
                                color: 'grey',
                                border: '1px solid grey',
                                boxShadow: 'none'
                            }
                        }}
                        data-testid="profile-picture-tool-button-on-cancel">
                        {translate('cancel')}
                    </Button>
                </Stack>
            </Box>
        </Modal>
    )
}

export default ProfilePictureTool
